/**
 * Clerk env-loading guard for the Portal frontend (RLY-013).
 *
 * Standing order [04]: env-driven config must throw early in non-dev modes.
 * `VITE_CLERK_PUBLISHABLE_KEY` is the auth-system root key — without it the
 * app cannot meaningfully render any signed-in surface, so we treat its
 * absence the same as the API base URL: throw at boot, not silently degrade.
 *
 * In DEV (`import.meta.env.DEV === true`) we permit a single explicit escape
 * hatch: if `VITE_ALLOW_NO_AUTH === "true"`, we return `null` and the caller
 * mounts the app in "no-auth shell" mode. The escape hatch logs a warning so
 * it is impossible to ship by accident — and the `import.meta.env.DEV` gate
 * means production bundles literally cannot enter that branch (Vite drops
 * the dead code at build time).
 *
 * No `?? "pk_test_..."`-style fallback. Ever.
 */

export interface ClerkEnvLoadResult {
  /** The publishable key when present, or `null` when DEV escape hatch is active. */
  publishableKey: string | null;
}

export class MissingClerkPublishableKeyError extends Error {
  constructor() {
    super(
      "VITE_CLERK_PUBLISHABLE_KEY is not set. Copy `apps/portal-frontend/.env.example` " +
        "to `.env.local`, fill in the publishable key from Clerk Dashboard → API Keys, " +
        "and restart the dev server. In CI/production the key MUST be present at build time. " +
        "If you intentionally want to mount the app without auth in DEV, set `VITE_ALLOW_NO_AUTH=true`.",
    );
    this.name = "MissingClerkPublishableKeyError";
  }
}

/**
 * Resolve the Clerk publishable key from a Vite-style env shape.
 *
 * Exposed as a pure function (not a side-effecting top-level read) so tests
 * can exercise every branch without monkey-patching `import.meta.env`.
 */
export function loadClerkPublishableKey(env: {
  VITE_CLERK_PUBLISHABLE_KEY?: string;
  VITE_ALLOW_NO_AUTH?: string;
  DEV?: boolean;
}): ClerkEnvLoadResult {
  const key = env.VITE_CLERK_PUBLISHABLE_KEY?.trim();
  if (key) return { publishableKey: key };

  // No key. Allow the explicit DEV escape hatch; otherwise throw.
  const allowNoAuth = env.VITE_ALLOW_NO_AUTH === "true";
  if (env.DEV && allowNoAuth) {
    // Loud warning so accidental usage is unmissable in dev consoles.
    console.warn(
      "[clerk-env] VITE_ALLOW_NO_AUTH=true — mounting Portal frontend without Clerk. " +
        "Signed-in surfaces will not work. This branch is unreachable in production builds.",
    );
    return { publishableKey: null };
  }

  throw new MissingClerkPublishableKeyError();
}
